import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { createErrorMessage } from "@/utils/create-error-message";

export const browserTokenResponseSchema = z.object({
  data: z.object({
    browser: z.string().nullable(),
    confirm: z.union([z.literal("Success"), z.literal("Fail")]),
    device: z.string().nullable(),
    ip: z.string().nullable(),
  }),
  message: z.string(),
});

export type BrowserTokenResponse = z.infer<typeof browserTokenResponseSchema>;

const BROWSER_TOKEN_ENDPOINT = `${NIKE_URL}/browser-token`;

export const validateBrowserToken = async ({
  browserToken,
  backendToken,
}: {
  browserToken: string;
  backendToken: string;
}): Promise<BrowserTokenResponse["data"]> => {
  if (!browserToken) throw new Error("Missing browser token");
  if (!backendToken) throw new Error("Missing backend token");

  const res = await fetch(BROWSER_TOKEN_ENDPOINT, {
    method: "GET",
    headers: {
      "X-User-Profile": backendToken,
      "X-Browser-Token": browserToken,
    },
  });

  if (!res.ok) {
    if (res.status === 400) {
      throw new Error("Missing browser token header");
    }
    // For 403, we still want to parse the response as it contains device info
    if (res.status !== 403) {
      const errorMessage = await createErrorMessage(
        BROWSER_TOKEN_ENDPOINT,
        res
      );
      throw new Error(errorMessage);
    }
  }

  return browserTokenResponseSchema.parse(await res.json()).data;
};

export const useValidateBrowserToken = ({
  browserToken,
}: {
  browserToken: string;
}) => {
  const { backendToken } = useAuth();
  return useQuery(
    ["browser-token", browserToken, backendToken],
    () =>
      validateBrowserToken({
        browserToken,
        backendToken: backendToken ?? "",
      }),
    {
      enabled: !!browserToken && !!backendToken,
      refetchInterval: 1000 * 20, // 20 seconds
      refetchIntervalInBackground: true,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );
};
